import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MessageContent, {
  SYSTEM_NOT_VISIBLE_TO_MEMBER,
} from 'Components/ComponentLibrary/Organisms/MessageList/MessageListItem/MessageContent';
import {
  ReactionList,
  useReactionList,
} from 'Components/ComponentLibrary/Components/Uncategorized/ReactionList';
import EditMessageButton
  from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/ThreadList/Thread/Body/Shared/Message/Shared/MessageOptionsMenu/EditMessageButton';
import moment from 'moment/moment';
import EditMessageCountdownTimer
  from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/ThreadList/Thread/Body/Shared/Message/Shared/MessageOptionsMenu/EditMessageCountdownTimer';
import {
  EditMessageInput,
} from 'Components/ComponentLibrary/Components/Uncategorized/EditMessageInput';
import { scrollToRef } from 'Helpers/ScrollToRef';
import { noop } from 'Helpers/PropTypes';
import { FDBox, FDStack } from '@facet-wealth/design';
import { RouteMessageButton } from 'Organisms/MessageList/MessageListItem/MessageRoute/RouteMessageButton';
import fr from 'facet-rest';
import LeftBorderedMessageListItem from 'Organisms/MessageList/MessageListItem/LeftBorderedMessageListItem';
import MessageLeftGutter from './MessageLeftGutter';
import MessageHeader from './MessageHeader';
import MessageSpacer from './MessageSpacer';

const MessageListItemNoMargin = ({
  messageResource,
  enableMessageEditing,
  onLoadComplete,
  dataTestAttributes,
  scrollRef,
}) => {
  const [editing, setEditing] = useState(false);
  const [editHeight, setEditHeight] = useState(0);
  const ref = useRef(null);
  const {
    reactionSummaries,
    onAddReaction,
    onRemoveReaction,
  } = useReactionList(messageResource.attr('reactions'));

  const canEdit = enableMessageEditing && moment().isBefore(messageResource.attr('editableUntil'));
  const showEditMessageInput = canEdit && editing;

  const handleEditButtonClick = () => {
    setEditing(true);
    scrollToRef(scrollRef, { behavior: 'smooth' });
  };

  useEffect(() => {
    if (ref.current) {
      setEditHeight(ref.current.clientHeight);
    }
  }, [ref.current]);

  if (messageResource.relationship(fr.relationships.threadRoutingDetails.name) || messageResource.attr('messageType') === SYSTEM_NOT_VISIBLE_TO_MEMBER) {
    return <LeftBorderedMessageListItem messageResource={messageResource} onLoadComplete={onLoadComplete} />;
  }

  return (
    <FDBox {...dataTestAttributes}>
      <FDStack direction={'column'} spacing={2}>
        <MessageSpacer />
        <FDBox paddingX={2}>
          <FDBox ref={scrollRef} />
          <FDStack direction={'row'} alignItems={'center'}>
            <MessageLeftGutter
              messageResource={messageResource}
              showAvatar
            />
            <MessageHeader messageResource={messageResource} />
            {canEdit && (
              <>
                  {!editing ? (
                    <EditMessageButton
                      toggleEditMessageInput={() => handleEditButtonClick()}
                      disableEdit={!canEdit}
                    />
                  ) : (
                    <EditMessageCountdownTimer
                      messageResource={messageResource}
                      onComplete={() => setEditing(false)}
                    />
                  )}
              </>
            )}
          </FDStack>
        </FDBox>
        <FDBox paddingX={2}>
          <FDStack direction={'column'} paddingY={2}>
            {!showEditMessageInput ? (
              <div ref={ref}>
                <MessageContent
                  messageResource={messageResource}
                  onLoadComplete={() => onLoadComplete()}
                />
              </div>
            ) : (
              <EditMessageInput
                messageResource={messageResource}
                onClose={() => setEditing(false)}
                rounded
                inputMinHeight={editHeight}
              />
            )}
            <FDStack direction={'row'} spacing={1} alignItems={'flex-end'}>
              <ReactionList
                reactions={reactionSummaries}
                onAddReaction={(emoji) => onAddReaction(emoji)}
                onRemoveReaction={(emoji) => onRemoveReaction(emoji)}
              />
              {messageResource.withRelationship(
                fr.relationships.manualMessageRouting.name,
                (rel) => <RouteMessageButton relationship={rel} />,
                () => null,
              )}
            </FDStack>
          </FDStack>
          <MessageSpacer />
        </FDBox>
      </FDStack>
    </FDBox>
  );
};

MessageListItemNoMargin.defaultProps = {
  enableMessageEditing: false,
  onLoadComplete: noop,
  dataTestAttributes: {},
  scrollRef: null,
};

MessageListItemNoMargin.propTypes = {
  messageResource: PropTypes.object.isRequired,
  enableMessageEditing: PropTypes.bool,
  onLoadComplete: PropTypes.func,
  dataTestAttributes: PropTypes.object,
  scrollRef: PropTypes.object,
};


export default MessageListItemNoMargin;
